import { Box, FormikSelectInputNative, FormikRadioGroup, FormLabel, BoxProps } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { AvlFilterOption, ProgramType, VendorEquipmentTypes } from 'types';

import { useGetAvlItemsQuery } from '../../services/approvedVendorList';
import FormSection from '../Forms/FormSection';

import {
  filterDcEligibleAvlItems,
  mapNonDcAvlManufacturerOptions,
  maybePrependCustomFreeTextOption,
  Option,
} from './AvlFormUtils';
import { MountingTypeAlert } from './MountingTypeAlert';

export interface MountingHardwareProps {
  className?: string;
  hideTitleAndDescription?: boolean;
  isLoading?: boolean;
  boxProps?: BoxProps;
  programType: ProgramType;
  readOnly?: boolean;
  hideMountingType?: boolean;
  isItcSystemDesignCompleted?: boolean;
  avlFilterOption?: AvlFilterOption;
  orgAlias?: string;
}

const mountingTypeOptions: Option[] = [
  { value: 'roof', label: 'Roof Mount', id: 'roof' },
  { value: 'ground', label: 'Ground Mount', id: 'ground' },
  { value: 'combination', label: 'Roof/Ground Combination', id: 'combination' },
];

export const MountingHardwareFormSection = ({
  className,
  hideTitleAndDescription = false,
  isLoading = false,
  boxProps,
  programType,
  readOnly = false,
  hideMountingType = false,
  isItcSystemDesignCompleted = false,
  avlFilterOption,
  orgAlias,
}: MountingHardwareProps) => {
  const { isSubmitting, values } = useFormikContext<any>();
  const { id: accountId } = useParams<{ id: string }>();
  // Server derives effectiveDate from accountId; never sent by the client.
  const { data: mountingData, isLoading: isAppSettingsLoading } = useGetAvlItemsQuery(
    accountId
      ? { accountId, equipmentType: VendorEquipmentTypes.mounting }
      : { programType, orgAlias, equipmentType: VendorEquipmentTypes.mounting },
  );
  const mounting = useMemo(() => {
    const items = mountingData?.data ?? [];
    // V2 has no server-side avlFilterOption; dcEligibleOnly is applied here instead.
    return avlFilterOption === AvlFilterOption.dcEligibleOnly
      ? filterDcEligibleAvlItems(items, VendorEquipmentTypes.mounting)
      : items;
  }, [mountingData, avlFilterOption]);

  const isAnythingLoadingOrSubmitting = isLoading || isAppSettingsLoading || isSubmitting;
  const boxValues = boxProps ?? { width: '100%', margin: 'sm' };
  const shouldHideMountingType = hideMountingType || isItcSystemDesignCompleted;

  const manufacturerOptions = useMemo(() => {
    const options = mapNonDcAvlManufacturerOptions(mounting);
    return maybePrependCustomFreeTextOption(options, values.mountingManufacturer);
  }, [mounting, values.mountingManufacturer]);

  return (
    <FormSection
      {...(!hideTitleAndDescription ? { title: 'Mounting', description: 'Optional for NTP' } : { className })}
    >
      <Box width="100%">
        <Box
          direction={{ base: 'column', tablet: 'row' }}
          childGap={{ base: 'lg', desktop: 'xl' }}
          width={boxValues.width}
          margin={boxValues.margin}
        >
          {!shouldHideMountingType && (
            <Box childGap={{ base: 'lg', desktop: 'lg' }} width="47%">
              <FormLabel inputId="mountingType">Mounting Type</FormLabel>
              <Field
                label="Mounting Type"
                name="mountingType"
                id="mountingType"
                component={FormikRadioGroup}
                options={mountingTypeOptions}
                isDisabled={readOnly || isAnythingLoadingOrSubmitting}
              />
            </Box>
          )}
          <Box alignContent="center" childGap="xl">
            {!isItcSystemDesignCompleted && (
              <Field
                label="Hardware Manufacturer"
                name="mountingManufacturer"
                id="mountingManufacturer"
                component={FormikSelectInputNative}
                options={manufacturerOptions}
                isDisabled={readOnly || isAnythingLoadingOrSubmitting}
                maxWidth="20em"
              />
            )}
            {hideMountingType && <MountingTypeAlert />}
          </Box>
        </Box>
      </Box>
    </FormSection>
  );
};
